import { supabase } from "./supabase";

export type Profile = {
  user_id: string;
  prenom: string | null;
  nom: string | null;
  telephone: string | null;
  avatar_url?: string | null;
  entreprise: string | null;
  pays: string | null;
  structure: "grosse" | "petite" | "independant" | null;
  tva_recup: "oui" | "non" | null;
  created_at?: string;
  updated_at?: string;
};

export type ProfileUpdate = {
  prenom?: string | null;
  nom?: string | null;
  telephone?: string | null;
  avatar_url?: string | null;
  entreprise?: string | null;
  pays?: string | null;
  structure?: "grosse" | "petite" | "independant" | null;
  tva_recup?: "oui" | "non" | null;
};

const AVATAR_BUCKET = "avatars";
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_MIME = ["image/jpeg", "image/png", "image/webp"] as const;

export type ProfileBasics = {
  prenom?: string | null;
  nom?: string | null;
  telephone?: string | null;
  avatar_url?: string | null;
};

/** Met à jour les champs profil (upsert partiel, aligné sur la ligne user). */
export async function updateProfile(userId: string, data: ProfileBasics): Promise<Profile> {
  return upsertProfile(userId, data);
}

/** URL publique de l’avatar enregistré en base, ou null. */
export async function getAvatarUrl(userId: string): Promise<string | null> {
  const p = await getProfile(userId);
  const u = p?.avatar_url?.trim();
  return u || null;
}

/**
 * Envoie l’image dans Storage (bucket `avatars`) et retourne l’URL publique.
 * Contraintes : JPEG, PNG, WebP, max 2 Mo.
 */
export async function uploadAvatar(userId: string, file: File): Promise<string> {
  if (!AVATAR_MIME.includes(file.type as (typeof AVATAR_MIME)[number])) {
    throw new Error("Format non pris en charge. Utilisez JPG, PNG ou WebP.");
  }
  if (file.size > AVATAR_MAX_BYTES) {
    throw new Error("L’image dépasse la taille maximale (2 Mo).");
  }
  const ext =
    file.type === "image/jpeg" ? "jpg" : file.type === "image/png" ? "png" : "webp";
  const path = `${userId}/avatar.${ext}`;
  const { error } = await supabase.storage.from(AVATAR_BUCKET).upload(path, file, {
    upsert: true,
    contentType: file.type,
    cacheControl: "3600",
  });
  if (error) throw error;
  const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

const ONBOARDING_DRAFT_KEY = "woahda_onboarding_draft";

export function getOnboardingDraft(): ProfileUpdate | null {
  try {
    const raw = localStorage.getItem(ONBOARDING_DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ProfileUpdate;
  } catch {
    return null;
  }
}

export function setOnboardingDraft(data: ProfileUpdate): void {
  localStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify(data));
}

export function clearOnboardingDraft(): void {
  localStorage.removeItem(ONBOARDING_DRAFT_KEY);
}

export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("user_id", userId)
    .single();
  if (error && error.code !== "PGRST116") throw error;
  return data as Profile | null;
}

export async function upsertProfile(userId: string, update: ProfileUpdate): Promise<Profile> {
  const { data, error } = await supabase
    .from("profiles")
    .upsert(
      { user_id: userId, ...update, updated_at: new Date().toISOString() },
      { onConflict: "user_id" }
    )
    .select()
    .single();
  if (error) throw error;
  return data as Profile;
}
